import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import {
  formatAmount,
  formatBuyerAddress,
  formatDate,
  stringToUpperCase,
} from "../../../../components/Helper";
import { SelfDocuments } from "../../../../components/Enums";

Font.register({
  family: "Times New Roman",
  fonts: [
    { src: "/fonts/Times-New-Roman.ttf" },
    { src: "/fonts/Times-New-Roman-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: "row",
  },
  page: {
    paddingHorizontal: 30,
    paddingTop: 60, // enough space for header
    paddingBottom: 50, // enough space for footer
    fontFamily: "Times New Roman",
    fontSize: 9,
    lineHeight: 1.15,
  },
  fullpage: {
    margin: 6,
    padding: 4,
  },
  section: {
    marginBottom: 6,
  },
  mainHeader: {
    position: "absolute",
    top: -70,
    left: 0,
    right: 0,
    height: 180,
    alignItems: "center",
    fontSize: 12,
    padding: 5,
  },
  header: {
    fontSize: 12,
    textAlign: "center",
    fontWeight: "bold",
    marginBottom: 20,
    textDecoration: "underline",
  },
  headerImage: {
    width: "90%",
    height: "100%",
  },
  regularFontSize: {
    fontSize: 9,
  },
  largeFontSize: {
    fontSize: 10,
  },
  boldText: {
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
  },
  footer: {
    position: "absolute",
    bottom: 5,
    left: 0,
    right: 0,
    height: 45,
    paddingHorizontal: 35, // was 40
    fontSize: 7.5, // was 8
    textAlign: "center",
  },
  textBlock: { fontSize: 6.7, paddingBottom: 1.5, paddingHorizontal: 3 },
  footerInline: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    textAlign: "center",
  },
  tableLeftCell: {
    width: 200,
    borderRight: 1,
    borderLeft: 1,
    padding: 3,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
  colonCell: {
    width: 20,
    borderRight: 1,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
});

const HealthAndQualityCertificate = ({
  salesInvoiceData,
  settingsData,
  companyData,
}: {
  salesInvoiceData: any;
  settingsData: any;
  companyData: any;
}) => {
  const productNames = salesInvoiceData?.sales_invoice_products
    ?.map((item: any) => stringToUpperCase(item?.product?.name))
    .join(" , ");

  const packingDetails = salesInvoiceData?.sales_invoice_products
    ?.map((item: any) => {
      const packingType = item?.packing_type?.name
        ? stringToUpperCase(item.packing_type.name)
        : "BAGS";

      const net = item?.packing_net_weight
        ? ` NET ${formatAmount(item.packing_net_weight, 3)} KGS`
        : "";

      const gross = item?.packing_gross_weight
        ? ` & GROSS ${formatAmount(item.packing_gross_weight, 3)} KGS EACH BAG`
        : "";

      return `(PACKED IN ${packingType})${net}${gross}`;
    })
    .join(" , ");

  const documentInputFields =
    salesInvoiceData?.my_documents_inputs?.[
      SelfDocuments.HEALTH_AND_QUALITY_CERTIFICATE
    ];

  const notifyParty = salesInvoiceData?.is_notify_party
    ? salesInvoiceData?.notify_party
    : salesInvoiceData?.buyer;

  let portOfLoading = "-";
  if (
    salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.name,
    )}, ${stringToUpperCase(salesInvoiceData?.origin_port?.country)}`;
  } else if (
    salesInvoiceData?.origin_port?.name &&
    !salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(salesInvoiceData?.origin_port?.name)}`;
  } else if (
    !salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.country,
    )}`;
  }

  return (
    <Document>
      <Page style={styles.page} size="A4">
        <View style={styles.mainHeader} fixed>
          {companyData?.invoice_header && (
            <Image
              src={`${process.env.REACT_APP_BACKEND_URL}${companyData?.invoice_header}`}
              style={[
                {
                  width:
                    companyData?.invoice_header_width ||
                    styles.headerImage.width,
                  height:
                    companyData?.invoice_header_height ||
                    styles.headerImage.height,
                },
              ]}
            />
          )}
        </View>

        <View style={[styles.fullpage]}>
          {/* HEADER */}
          <View style={styles.mainContainer}>
            <Text
              style={[
                styles.header,
                styles.boldText,
                {
                  flex: 2,
                  width: 200,
                },
              ]}
            >
              HEALTH / QUALITY CERTIFICATE
            </Text>
          </View>

          {/* REPORT No and Date */}
          <View style={styles.section}>
            <View style={styles.mainContainer}>
              <Text
                style={[styles.boldText, styles.regularFontSize, { flex: 1 }]}
              >
                REPORT NO.{" "}
                <Text style={styles.boldText}>
                  {stringToUpperCase(
                    salesInvoiceData?.my_documents_inputs
                      ?.HEALTH_AND_QUALITY_CERTIFICATE?.certificate_no,
                  )}
                </Text>
              </Text>
              <Text style={[styles.boldText, styles.regularFontSize]}>
                DATE:{" "}
                <Text style={styles.boldText}>
                  {" "}
                  {formatDate(
                    salesInvoiceData?.my_documents_inputs
                      ?.HEALTH_AND_QUALITY_CERTIFICATE?.certificate_date,
                  )}
                </Text>
              </Text>
            </View>
          </View>

          {/* CERTIFICATION */}
          <View style={styles.section}>
            <Text style={styles.regularFontSize}>
              THIS IS TO CERTIFY THAT AT THE REQUEST OF MS LAXMI ENTERPRISE,
              GUJARAT, INDIA. THE{" "}
              <Text style={[styles.largeFontSize, styles.boldText]}>
                {stringToUpperCase(productNames)}
              </Text>{" "}
              SAMPLES WERE TESTED PERTAINS TO THE FOLLOWING CONSIGNMENT
              FURNISHED BY THE SHIPPER.
            </Text>
          </View>

          {/* TABLE */}
          <View>
            {/* TABLE ROWS */}
            <View style={[styles.tableRow, { border: 1 }]}>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 200,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                INV NO. & DATE
              </Text>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                  },
                ]}
              >
                {stringToUpperCase(salesInvoiceData?.invoice_no)} DATE:{" "}
                {formatDate(salesInvoiceData?.invoice_date)}
              </Text>
            </View>
            {/* 2 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 200,
                    borderRight: 1,
                    borderLeft: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                B/L NOS.
              </Text>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {salesInvoiceData?.bl_no ? salesInvoiceData?.bl_no : "-"}
              </Text>
            </View>
            {/* 3 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  SHIPPER
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>

              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {stringToUpperCase(settingsData?.company_name)}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(
                    settingsData?.company_address?.slice(0, 13),
                  )}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(
                    settingsData?.company_address?.slice(14, 37),
                  )}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(settingsData?.company_address?.slice(40))}
                </Text>
              </View>
            </View>
            {/* 4 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  CONSIGNEE
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {stringToUpperCase(salesInvoiceData?.buyer?.name)}
                </Text>
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {formatBuyerAddress(salesInvoiceData?.buyer, true)}
                </Text>
              </View>
            </View>
            {/* 5 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  NOTIFY PARTY
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {stringToUpperCase(notifyParty.name)}
                </Text>
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {formatBuyerAddress(notifyParty, true)}
                </Text>
              </View>
            </View>
            {/* 6 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 200,
                    borderRight: 1,
                    borderLeft: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                PORT OF LOADING
              </Text>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {portOfLoading}
              </Text>
            </View>
            {/* 7 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 200,
                    borderRight: 1,
                    borderLeft: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                PORT OF DESTINATION
              </Text>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {salesInvoiceData?.destination_port
                  ? stringToUpperCase(salesInvoiceData?.destination_port)
                  : "-"}
              </Text>
            </View>
            {/*8 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  VESSEL NAME
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {stringToUpperCase(
                  salesInvoiceData?.vessel_no
                    ? salesInvoiceData?.vessel_no
                    : "-",
                )}
              </Text>
            </View>
            {/* 9 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  CARGO DESCRIPTION
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {stringToUpperCase(productNames)}
              </Text>
            </View>
            {/* 10 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  QUANTITY
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {salesInvoiceData?.total_no_of_bags
                    ? `${salesInvoiceData?.total_no_of_bags} BAGS`
                    : ""}
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  NET WEIGHT:{" "}
                  {salesInvoiceData?.total_net_weight
                    ? formatAmount(salesInvoiceData?.total_net_weight / 1000, 3)
                    : "-"}{" "}
                  MTS
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  GROSS WEIGHT:{" "}
                  {salesInvoiceData?.total_gross_weight
                    ? formatAmount(
                        salesInvoiceData?.total_gross_weight / 1000,
                        3,
                      )
                    : "-"}{" "}
                  MTS
                </Text>

                {/* <Text style={{ paddingLeft: 4 }}>
                  {salesInvoiceData?.total_no_of_bags} BAGS
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  NET WEIGHT:
                  {formatAmount(
                    salesInvoiceData?.total_net_weight / 1000,
                    3
                  )}{" "}
                  MTS
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  GROSS WEIGHT:{" "}
                  {formatAmount(salesInvoiceData?.total_gross_weight / 1000, 3)}{" "}
                  MTS
                </Text> */}
              </View>
            </View>
            {/* 11 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  PACKING
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 3,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {packingDetails}
              </Text>
            </View>
            {/* 12 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={styles.tableLeftCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  CONTAINER NOS
                </Text>
              </View>
              <View style={styles.colonCell}>
                <Text style={[styles.regularFontSize, styles.boldText]}>:</Text>
              </View>

              <View
                style={[
                  {
                    flexDirection: "column",
                    flexWrap: "wrap",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 0,
                    paddingTop: 2,
                  },
                  styles.regularFontSize,
                ]}
              >
                {salesInvoiceData?.containers?.length > 0 && (
                  <Text
                    style={{
                      padding: 3,
                      textAlign: "left",
                      marginBottom: 0,
                    }}
                  >
                    {salesInvoiceData?.containers
                      ?.map((row: any) => stringToUpperCase(row?.container_no))
                      .join(", ")}
                  </Text>
                )}
              </View>
            </View>
          </View>

          {/* FINAL CERTIFICATION */}
          <View
            wrap={false}
            style={(styles.section, { marginTop: 10, marginBottom: 10 })}
          >
            <Text style={[styles.regularFontSize, styles.boldText]}>
              WE FOUND THE PRODUCT IS AN AGRICULTURAL COMMODITY. THE VISUAL
              EXAMINATION AND TEST RESULT SHOWS THAT THE SAMPLE IS FREE FROM ANY
              DEAD OR ALIVE WEEVILS INSECTS. THE LEVEL OF AFLATOXIN FOUND IN THE
              SAMPLE IS WITHIN THE PERMISSIBLE LIMIT OF FSSAI AND IS FOUND FIT
              FOR HUMAN CONSUMPTION.
            </Text>
          </View>

          {/* SIGNATURE */}
          <View
            wrap={false}
            style={[
              styles.section,
              {
                flexDirection: "row",
                justifyContent: "flex-end",
                marginRight: 20,
              },
            ]}
          >
            <View style={[{ alignItems: "center" }]}>
              <Text style={[styles.regularFontSize, { marginBottom: 40 }]}>
                For, {stringToUpperCase(documentInputFields?.lab_name)}
              </Text>
              <Text style={[styles.regularFontSize]}>AUTHORISED SIGNATORY</Text>
            </View>
          </View>
        </View>

        {/* Footer  */}
        <View style={[styles.footer]} fixed>
          <View style={[{ textAlign: "center" }]}>
            <Text style={[styles.boldText]}>
              {stringToUpperCase(settingsData?.company_name)}
            </Text>
            <Text style={[styles.textBlock]}>
              {stringToUpperCase(settingsData?.company_address)}
            </Text>
            <View style={styles.footerInline}>
              <Text style={[styles.textBlock]}>
                <Text style={styles.boldText}>Email : </Text>
                {stringToUpperCase(settingsData?.company_email)}
              </Text>
              <Text style={[styles.textBlock, { marginLeft: 5 }]}>
                <Text style={styles.boldText}>Phone No. : </Text>
                {stringToUpperCase(settingsData?.company_phone_1)} /{" "}
                {stringToUpperCase(settingsData?.company_phone_2)}
              </Text>
            </View>
            <View style={styles.footerInline}>
              <Text style={[styles.textBlock]}>
                <Text style={styles.boldText}>Subject : </Text>
                Subject
              </Text>
              <Text style={[styles.textBlock, { marginLeft: 5 }]}>
                <Text style={styles.boldText}>GSTIN No. : </Text>
                {stringToUpperCase(settingsData?.company_gst_no)}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default HealthAndQualityCertificate;
